import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Sparkles, ChevronDown, ChevronUp, Calendar, Stethoscope } from "lucide-react";

export function SessionRecapCard() {
  const [recaps, setRecaps] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/me/session-recaps?limit=10").then((r) => setRecaps(r.data)).catch(() => {});
  }, []);

  if (!recaps.length) return null;
  const latest = recaps[0];
  const older = recaps.slice(1);

  return (
    <section
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 lg:p-7"
      data-testid="home-session-recap"
    >
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] flex items-center justify-center">
          <Sparkles size={16} />
        </div>
        <div>
          <div className="uppercase-label">Your latest session recap</div>
          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5 flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Stethoscope size={11} /> {latest.doctor_name}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} /> {formatDate(latest.start_iso)}
            </span>
          </div>
        </div>
      </div>

      <RecapBody payload={latest.patient_recap} />

      {older.length > 0 && (
        <>
          <button
            data-testid="recap-toggle-history"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--mano-primary)] hover:underline"
          >
            {expanded ? "Hide" : "Show"} {older.length} earlier session{older.length === 1 ? "" : "s"}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expanded && (
            <div className="mt-4 space-y-3" data-testid="recap-history">
              {older.map((r) => (
                <details
                  key={r.id}
                  className="rounded-xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)] p-3"
                >
                  <summary className="cursor-pointer text-sm font-semibold flex items-center justify-between">
                    <span>{r.doctor_name} · {formatDate(r.start_iso)}</span>
                    <ChevronDown size={14} className="text-[color:var(--mano-ink-soft)]" />
                  </summary>
                  <div className="mt-3 pl-1">
                    <RecapBody payload={r.patient_recap} compact />
                  </div>
                </details>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RecapBody({ payload, compact = false }) {
  if (!payload || !payload.recap) return null;
  const lang = payload.language || "en";
  const NATIVE_LABEL = {
    en: null,
    hi: "हिन्दी में",
    ta: "தமிழில்",
    te: "తెలుగులో",
    kn: "ಕನ್ನಡದಲ್ಲಿ",
    mr: "मराठीत",
    bn: "বাংলায়",
  };
  const STEPS_LABEL = {
    en: "Next steps", hi: "अगले कदम", ta: "அடுத்த படிகள்",
    te: "తదుపరి దశలు", kn: "ಮುಂದಿನ ಹಂತಗಳು",
    mr: "पुढील पावले", bn: "পরবর্তী পদক্ষেপ",
  };
  const CHECKIN_LABEL = {
    en: "Check in", hi: "अगला चेक-इन", ta: "மறு சந்திப்பு",
    te: "తదుపరి చెక్-ఇన్", kn: "ಮುಂದಿನ ಚೆಕ್-ಇನ್",
    mr: "पुढील तपासणी", bn: "পরবর্তী চেক-ইন",
  };
  return (
    <div className={compact ? "mt-2" : "mt-4"} lang={lang}>
      {NATIVE_LABEL[lang] && (
        <span
          className="inline-block text-[10px] tracking-wider uppercase text-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]/10 rounded-full px-2 py-0.5 mb-2"
          data-testid="recap-language-badge"
        >
          {NATIVE_LABEL[lang]}
        </span>
      )}
      <p className="text-[color:var(--mano-ink)] leading-relaxed">{payload.recap}</p>

      {payload.next_steps?.length > 0 && (
        <div className="mt-4">
          <div className="uppercase-label">{STEPS_LABEL[lang] || STEPS_LABEL.en}</div>
          <ul className="mt-2 space-y-1.5">
            {payload.next_steps.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--mano-primary)] shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {payload.followup && (
        <div className="mt-4 text-sm bg-[color:var(--mano-primary)]/5 border border-[color:var(--mano-primary)]/20 text-[color:var(--mano-primary)] rounded-xl px-3 py-2">
          <b>{CHECKIN_LABEL[lang] || CHECKIN_LABEL.en}:</b> {payload.followup}
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
